/**
 * The approach corridor and the docking guides (M2 spec §7.2): a cylinder out of the bay mouth along the bay's
 * out axis, `corridor.length` long, with holographic rings every `corridor.ringSpacing` metres from the far end,
 * the corridor and bay speed limits, and the nose, roll and pad readouts the HUD and dock detection read.
 *
 * Everything here runs every tick or frame: allocation-free, module scratch only (D-014). Positions are float64
 * world metres (D-011).
 */
import { Vec3Scratch, type Vec3 } from '../core/math'
import type { DockingConfig } from '../data/m2Schemas'
import type { BayFrame, DockingReadout } from '../space/contracts'
import { bayLocalOf, pointInBayOf } from './bay'

const RAD_TO_DEG = 180 / Math.PI
/** Below this length a direction is treated as degenerate (roll is unmeasurable when the nose is along the bay's up). */
const DEGENERATE = 1e-6
/** Slack for the ring count, so 1500 / 100 gives 15 rings despite rounding. */
const RING_EPS = 1e-9

/**
 * Radius of the corridor cylinder and its rings: `corridor.radiusMult` × the half-diagonal of the bay mouth, so a
 * ring frames the whole mouth.
 */
export function corridorRadius(frame: Readonly<BayFrame>, cfg: Pick<DockingConfig, 'corridor'>): number {
  return cfg.corridor.radiusMult * 0.5 * Math.sqrt(frame.width * frame.width + frame.height * frame.height)
}

/** Number of guide rings: one every `ringSpacing` m from `length` down to the first ring in front of the mouth. */
export function corridorRingCount(cfg: Pick<DockingConfig, 'corridor'>): number {
  return Math.floor(cfg.corridor.length / cfg.corridor.ringSpacing + RING_EPS)
}

/**
 * Centre of guide ring `k` (0 = the far end, `corridor.length` from the mouth; each next ring `ringSpacing`
 * closer), float64 world, into `out`. The ring's plane is perpendicular to `frame.out`.
 */
export function corridorRing(frame: Readonly<BayFrame>, cfg: Pick<DockingConfig, 'corridor'>, k: number, out: Vec3): Vec3 {
  const d = cfg.corridor.length - k * cfg.corridor.ringSpacing
  out.x = frame.mouth.x + frame.out.x * d
  out.y = frame.mouth.y + frame.out.y * d
  out.z = frame.mouth.z + frame.out.z * d
  return out
}

/**
 * A float result with its own map (DA-1, D-TBD; D-1052's `leadDist.value`): a float returned from a call V8 does not
 * inline is boxed (16 B), one stored into a number-initialised class field is not.
 */
export class FloatScratch {
  value = 0
}

/** Nose error into `out.value` (`noseError`, bit for bit). Allocation-free. */
export function noseErrorTo(frame: Readonly<BayFrame>, fwd: Readonly<Vec3>, out: FloatScratch): void {
  // Inward = −out: cos = −(fwd·out), |sin| = |fwd × out|.
  const o = frame.out
  const cx = fwd.y * o.z - fwd.z * o.y
  const cy = fwd.z * o.x - fwd.x * o.z
  const cz = fwd.x * o.y - fwd.y * o.x
  out.value = Math.atan2(Math.sqrt(cx * cx + cy * cy + cz * cz), -(fwd.x * o.x + fwd.y * o.y + fwd.z * o.z))
}

const result = new FloatScratch()

/** Nose error: the angle between the ship's forward and the bay's inward axis (−out), radians. */
export function noseError(frame: Readonly<BayFrame>, fwd: Readonly<Vec3>): number {
  noseErrorTo(frame, fwd, result)
  return result.value
}

/**
 * Signed roll error about the ship's forward axis, radians in (−π, π]: positive when the ship is rolled right
 * (clockwise seen from behind, the `Control.roll` sense) of the bay's up. Measured against the bay's up projected
 * onto the plane across the nose, so a pitch error does not read as roll; 0 when the nose points along the
 * bay's up (roll is undefined there).
 */
export function rollError(frame: Readonly<BayFrame>, fwd: Readonly<Vec3>, up: Readonly<Vec3>): number {
  rollErrorTo(frame, fwd, up, result)
  return result.value
}

/** `rollError` into `out.value` (bit for bit): the autopilot's per-tick form (DA-1). Allocation-free. */
export function rollErrorTo(frame: Readonly<BayFrame>, fwd: Readonly<Vec3>, up: Readonly<Vec3>, out: FloatScratch): void {
  const bu = frame.up
  const d = bu.x * fwd.x + bu.y * fwd.y + bu.z * fwd.z
  let rx = bu.x - d * fwd.x
  let ry = bu.y - d * fwd.y
  let rz = bu.z - d * fwd.z
  const rl = Math.sqrt(rx * rx + ry * ry + rz * rz)
  if (rl < DEGENERATE) {
    out.value = 0
    return
  }
  rx /= rl
  ry /= rl
  rz /= rl
  // θ from the reference to the ship's up about fwd: sin = fwd·(ref × up), cos = ref·up.
  const cx = ry * up.z - rz * up.y
  const cy = rz * up.x - rx * up.z
  const cz = rx * up.y - ry * up.x
  out.value = Math.atan2(fwd.x * cx + fwd.y * cy + fwd.z * cz, rx * up.x + ry * up.y + rz * up.z)
}

const local: Vec3 = new Vec3Scratch()
/** `corridorState`'s nose and roll errors (apart from the wrappers' `result`). */
const stateErr = new FloatScratch()

/**
 * Fills the geometric fields of the docking readout for a ship at `pos` (float64 world) moving at `vel`, with unit
 * world axes `fwd` (nose, local −Z) and `up` (local +Y): `along` (metres from the mouth along the out axis;
 * negative inside the bay), `lateral` (metres from the axis), `inCorridor`, `inBay`, `speed`, `speedLimit` (bay
 * inside the bay, corridor inside the corridor, else 0), `alignDeg` (nose vs inward), `rollDeg` (signed, see
 * `rollError`) and `padDistance`. Leaves `phase`, `station`, `clearanceLeft` and `holdProgress` alone.
 * Allocation-free.
 */
export function corridorState(
  frame: Readonly<BayFrame>,
  cfg: Pick<DockingConfig, 'corridor' | 'speedLimits'>,
  pos: Readonly<Vec3>,
  vel: Readonly<Vec3>,
  fwd: Readonly<Vec3>,
  up: Readonly<Vec3>,
  out: DockingReadout,
): DockingReadout {
  // Objects only across the calls, the errors through `stateErr` (the autopilot's `*Of`/`*To` forms, bit for bit): a
  // float passed to or returned from a call V8 does not inline is boxed (DA-1 follow-up, D-TBD).
  bayLocalOf(frame, pos, local)
  const along = local.z - frame.depth * 0.5
  const lateral = Math.sqrt(local.x * local.x + local.y * local.y)
  const inBay = pointInBayOf(frame, pos)
  const inCorridor = along >= 0 && along <= cfg.corridor.length && lateral <= corridorRadius(frame, cfg)
  out.along = along
  out.lateral = lateral
  out.inBay = inBay
  out.inCorridor = inCorridor
  out.speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z)
  out.speedLimit = inBay ? cfg.speedLimits.bay : inCorridor ? cfg.speedLimits.corridor : 0
  noseErrorTo(frame, fwd, stateErr)
  out.alignDeg = stateErr.value * RAD_TO_DEG
  rollErrorTo(frame, fwd, up, stateErr)
  out.rollDeg = stateErr.value * RAD_TO_DEG
  const padx = pos.x - frame.pad.x
  const pady = pos.y - frame.pad.y
  const padz = pos.z - frame.pad.z
  out.padDistance = Math.sqrt(padx * padx + pady * pady + padz * padz)
  return out
}
